import type { ComponentTransformSnapshot } from '../../component-instance';

/**
 * 刚体位姿不变量校验容差。
 *
 * 说明：直接流形步进下缩放应严格恒等、四元数严格单位长度；此处留极小容差，
 * 仅为兜住嵌套帧变换那一次矩阵往返可能引入的 ~1e-12 级噪声，绝不放过真实漂移。
 */
export const STANDARD_MATE_RIGID_INVARIANT_EPSILON = 1e-9;

/**
 * 刚体位姿不变量违例。
 */
export interface StandardMateRigidInvariantViolation {
  /** 违例组件实例 ID。 */
  readonly componentInstanceId: string;
  /** 违例类型：缩放漂移或四元数非单位长度。 */
  readonly kind: 'scale-drift' | 'quaternion-denormalized';
  /** 求解前的值（缩放三元组或四元数模长）。 */
  readonly before: number | readonly [number, number, number];
  /** 求解后的值。 */
  readonly after: number | readonly [number, number, number];
  /** 相对不变量基准的最大偏差。 */
  readonly maxDeviation: number;
}

/**
 * 校验一次配合求解是否保持了刚体位姿不变量（B 方案自执行守卫）。
 *
 * 设计说明：
 * 配合求解只对零件做刚体运动，因此“缩放不变”与“四元数单位长度”是必须恒成立的不变量。缩放漂移问题的
 * 根因是求解反复 compose/decompose 往返把误差渗进这些不变量并逐帧放大。仅在 {@link applyWorldDeltaToTransform}
 * 一个口子收口，无法阻止未来新增的、绕过该口子的位姿写回路径重新引入漂移。此守卫把不变量显式化：
 * 求解后逐组件比对求解前/后的缩放与四元数模长，任何越界都视为违例返回。测试对回放场景断言“零违例”，
 * dev 构建在求解链路里对违例 `console.error` 告警——从而任何未来的漂移路径会**立刻在测试/开发期炸出来**，
 * 而不是在生产里悄悄累积到把配合顶破。这不是靠人记得保留缩放，而是靠守卫失败强制暴露。
 *
 * @param input 求解前/后组件位姿表与容差。
 * @returns 违例列表；无违例时为空数组。
 */
export function collectStandardMateRigidInvariantViolations(input: {
  /** 求解前组件 local transform 表。 */
  readonly before: Readonly<Record<string, ComponentTransformSnapshot>>;
  /** 求解后组件 local transform 表。 */
  readonly after: Readonly<Record<string, ComponentTransformSnapshot>>;
  /** 校验容差；缺省用 {@link STANDARD_MATE_RIGID_INVARIANT_EPSILON}。 */
  readonly epsilon?: number;
}): StandardMateRigidInvariantViolation[] {
  const epsilon = input.epsilon ?? STANDARD_MATE_RIGID_INVARIANT_EPSILON;
  const violations: StandardMateRigidInvariantViolation[] = [];

  for (const [componentInstanceId, afterTransform] of Object.entries(input.after)) {
    const beforeTransform = input.before[componentInstanceId];
    if (!beforeTransform) {
      continue;
    }

    const scaleDeviation = resolveMaxScaleDeviation(beforeTransform.scale, afterTransform.scale);
    if (scaleDeviation > epsilon) {
      violations.push({
        componentInstanceId,
        kind: 'scale-drift',
        before: [...beforeTransform.scale] as [number, number, number],
        after: [...afterTransform.scale] as [number, number, number],
        maxDeviation: scaleDeviation,
      });
    }

    const beforeNorm = resolveQuaternionNorm(beforeTransform.quaternion);
    const afterNorm = resolveQuaternionNorm(afterTransform.quaternion);
    const normDeviation = Math.abs(afterNorm - 1);
    // 只在求解“把四元数搞成非单位”时报违例：求解前本就非单位（外部数据问题）不归求解器。
    if (normDeviation > epsilon && normDeviation > Math.abs(beforeNorm - 1) + epsilon) {
      violations.push({
        componentInstanceId,
        kind: 'quaternion-denormalized',
        before: beforeNorm,
        after: afterNorm,
        maxDeviation: normDeviation,
      });
    }
  }

  return violations;
}

/** 计算两组缩放的逐轴最大绝对偏差。 */
function resolveMaxScaleDeviation(
  before: readonly [number, number, number],
  after: readonly [number, number, number],
): number {
  return Math.max(
    Math.abs(after[0] - before[0]),
    Math.abs(after[1] - before[1]),
    Math.abs(after[2] - before[2]),
  );
}

/** 计算四元数模长。 */
function resolveQuaternionNorm(
  quaternion: readonly [number, number, number, number],
): number {
  return Math.hypot(quaternion[0], quaternion[1], quaternion[2], quaternion[3]);
}
